'use client';

import UIButton from 'components/buttons/UIButton';
import { useAppSelector } from 'lib/hooks';
import { enrollSunfireApplicant } from 'actions/Enroll';
import { MedicareApiPlan, SunfireGapPlan, SunfirePlanDetails } from 'types/api';
import { useTransition, useState } from 'react';
import MedigapEnrollmentModal from './MedigapEnrollmentModal';
import { selectEnrollmentData } from 'slices/selectors';
import { useUser } from '@auth0/nextjs-auth0';
import { selectCartPlans } from 'slices/cartSlice';
import { useRouter } from 'next/navigation';
import {
  captureAccountSignupStarted,
  captureEnrollmentFailed,
  captureEnrollmentStarted,
} from 'lib/analytics';
import { writeSessionItem } from 'lib/safeSessionStorage';

interface EnrollmentHandlerProps {
  plan: MedicareApiPlan;
  label: string;
  onError?: (error: string) => void;
  disabled?: boolean;
}

export default function EnrollmentHandler({
  plan,
  label,
  onError,
  disabled = false,
}: EnrollmentHandlerProps) {
  const [isPending, startTransition] = useTransition();
  const [showModal, setShowModal] = useState(false);

  const { user } = useUser();
  const router = useRouter();

  const cartPlans = useAppSelector(selectCartPlans);

  const formatPlanType = (type: string) => {
    const formatted = type.toUpperCase();

    if (formatted === 'PDP') {
      return 'PD';
    }

    return formatted;
  };

  // Get user data from Redux
  const { firstName, lastName, location, gender, birthday } =
    useAppSelector(selectEnrollmentData);

  const planDetails =
    plan.type === 'gap'
      ? (plan.plan as SunfireGapPlan)
      : (plan.plan_details as SunfirePlanDetails);

  const handleEnrollment = () => {
    captureEnrollmentStarted({
      plan_type: plan.type,
      // Match plan_added_to_cart: internal plan id for funnel joins.
      plan_id: plan.id,
    });

    if (plan.type === 'gap') {
      setShowModal(true);
      return;
    }

    startTransition(async () => {
      // Create FormData with all required fields for Sunfire enrollment
      const formData = new FormData();

      // User information from auth
      formData.append('applicant.firstName', firstName ?? '');
      formData.append('applicant.lastName', lastName ?? '');

      // Date of birth (matching playground structure)
      if (birthday) {
        // Ensure we have valid birthdate format
        const [year, month, day] = (birthday as string).split('-');

        formData.append('applicant.dob.year', year);
        formData.append('applicant.dob.month', month);
        formData.append('applicant.dob.day', day);
      }

      // Location information
      formData.append('applicant.home.zip', location.zipcode);
      formData.append('applicant.home.fips', location.countyFips);

      // Additional eligibility information
      formData.append('applicant.gender', gender);

      // Plan information (matching playground structure)
      formData.append('metadata.route.planId', plan.external_id);
      formData.append('metadata.route.prodType', formatPlanType(plan.type));
      formData.append(
        'metadata.route.carrierId',
        planDetails.carrierId.toString()
      );
      formData.append('metadata.route.fips', location.countyFips);

      // Call the enrollment action
      const result = await enrollSunfireApplicant(null, formData);
      if (result?.error) {
        captureEnrollmentFailed({
          plan_type: plan.type,
          plan_id: plan.id,
          status: result.status,
          reason: result.reason,
        });
        onError?.(result.error);
      }
    });
  };

  const handleNeedsAccount = () => {
    if (typeof window !== 'undefined') {
      writeSessionItem('cart', JSON.stringify(cartPlans));
    }

    captureAccountSignupStarted('enrollment');
    router.push(`/auth/login?returnTo=/cart&screen_hint=signup`);
  };

  if (!user) {
    return (
      <UIButton
        buttonType="primary"
        label={label}
        onClick={handleNeedsAccount}
        fullWidth={true}
      />
    );
  }

  return (
    <>
      <UIButton
        buttonType="primary"
        label={isPending ? 'Enrolling...' : label}
        onClick={handleEnrollment}
        disabled={disabled || isPending}
        fullWidth={true}
      />
      <MedigapEnrollmentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        planName={plan.name || 'Medicare Supplement Plan'}
        phoneNumber="(708) 719-9143"
      />
    </>
  );
}
